import { useEffect, useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { apiClient, ApiError } from "../api/client";
import type { Organization } from "../api/types";
import { useAuth } from "../auth/AuthContext";

export function LoginPage() {
  const { user, checking, login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  if (!checking && user) return <AuthenticatedLanding requestedPath={(location.state as { from?: string } | null)?.from} />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(username.trim(), password);
      const requestedPath = (location.state as { from?: string } | null)?.from;
      if (requestedPath?.startsWith("/")) navigate(requestedPath, { replace: true });
      else {
        const organizations = await apiClient.listOrganizations().catch(() => []);
        navigate(organizationLandingPath(organizations), { replace: true });
      }
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "登录失败，请稍后重试");
    } finally { setBusy(false); }
  }

  return (
    <main className="login-page">
      <div className="login-container">
        <div className="brand login-brand"><span className="brand-mark"><i /><i /><i /></span><span><strong>DeepVision</strong><small>数据标注平台</small></span></div>
        <section className="login-panel">
          <form className="login-card" onSubmit={submit}>
            <h1>登录数据平台</h1><p className="muted">使用团队账号登录</p>
            <label>用户名<input name="username" autoComplete="username" required value={username} onChange={(e) => setUsername(e.target.value)} /></label>
            <label>密码<input name="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} /></label>
            {error && <div className="inline-error" role="alert">{error}</div>}
            <button className="button primary wide" disabled={busy || !username.trim() || !password} type="submit">{busy ? "正在登录…" : "登录"}</button>
          </form>
        </section>
      </div>
    </main>
  );
}

function AuthenticatedLanding({ requestedPath }: { requestedPath?: string }) {
  const [target, setTarget] = useState("");
  useEffect(() => {
    if (requestedPath?.startsWith("/")) { setTarget(requestedPath); return; }
    let active = true;
    void apiClient.listOrganizations()
      .then((organizations) => { if (active) setTarget(organizationLandingPath(organizations)); })
      .catch(() => { if (active) setTarget("/organizations"); });
    return () => { active = false; };
  }, [requestedPath]);
  return target ? <Navigate to={target} replace /> : <main className="login-page"><p>正在进入组织…</p></main>;
}

export function organizationLandingPath(organizations: Array<Pick<Organization, "id">>): string {
  return organizations[0] ? `/organizations/${organizations[0].id}/projects` : "/organizations";
}
